import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { jobsAPI, evaluationsAPI, discAPI, jobPositionsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Loader2, 
  ArrowLeft, 
  Briefcase, 
  Star,
  Target,
  Heart,
  Brain,
  Lightbulb,
  Crown,
  Award,
  CheckCircle,
  User,
  Users,
  ChevronRight,
  ChevronLeft,
  ClipboardList,
  BarChart3,
  FileText,
  AlertCircle,
  Plus,
  X
} from 'lucide-react';
import { Separator } from '../../components/ui/separator';

// Icons for evaluation levels
const getNivelIcon = (nivel) => {
  const icons = {
    'entrega': Target,
    'comportamento': Heart,
    'cognitivo': Brain,
    'aspiracao': Lightbulb,
    'experiencia': Award,
    'lideranca': Crown
  };
  return icons[nivel] || Target;
};

// DISC colors
const DISC_COLORS = {
  'D': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  'I': { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  'S': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  'C': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' }
};

export default function CreateJob() {
  const navigate = useNavigate();
  const location = useLocation();
  const { jobId } = useParams(); // For edit mode
  const isEditMode = !!jobId;
  
  const [loading, setLoading] = useState(false);
  const [loadingJob, setLoadingJob] = useState(false);
  const [evaluationTemplate, setEvaluationTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  
  // Job Positions (Engenharia de Cargo) state
  const [jobPositions, setJobPositions] = useState([]);
  const [selectedPositionId, setSelectedPositionId] = useState('manual');
  const [loadingPositions, setLoadingPositions] = useState(false);
  
  // DISC Questionnaire state
  const [discQuestionnaire, setDiscQuestionnaire] = useState(null);
  const [loadingQuestionnaire, setLoadingQuestionnaire] = useState(false);
  const [nivelHierarquico, setNivelHierarquico] = useState('pleno');
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState({});
  const [questionnaireStep, setQuestionnaireStep] = useState(0); // 0=nivel select, 1-25=questions, 26=result
  const [calculatedProfile, setCalculatedProfile] = useState(null);
  const [questionnaireMode, setQuestionnaireMode] = useState('simple'); // 'simple' or 'questionnaire'
  
  // Selected candidates state
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [candidatesStats, setCandidatesStats] = useState({ total: 0, aprovados: 0 });
  
  // Linked job position (cargo) details
  const [linkedPosition, setLinkedPosition] = useState(null);
  
  const [form, setForm] = useState({
    titulo: '',
    descricao: '',
    localizacao: '',
    tipo_contrato: 'CLT',
    publica: true,
    destaque: false,
    data_expiracao: '',
    perfil_ideal: null,
    cargo_id: null, // Reference to job position
    num_vagas: 1, // Quantidade de posições
    // Novos campos
    salario: '',
    horario_trabalho: '',
    beneficios: [],
    observacoes: '',
    // Avaliação Big Five
    bigfive_required: 'none' // 'none', 'BFI-44', 'NEO-PI-R'
  });

  // Estado para novo benefício
  const [newBeneficio, setNewBeneficio] = useState('');

  // Initialize empty ideal profile from template
  const initializePerfilIdeal = (template) => {
    if (!template) return null;
    
    // Handle both array format (from API) and object format
    let niveisData;
    if (Array.isArray(template.niveis)) {
      // API returns { niveis: [...] }
      niveisData = template.niveis;
    } else if (Array.isArray(template)) {
      // Direct array
      niveisData = template;
    } else if (typeof template === 'object') {
      // Object format { nivel1: {...}, nivel2: {...} }
      niveisData = Object.entries(template).map(([key, nivel]) => ({
        nivel: key,
        ...nivel
      }));
    } else {
      return null;
    }
    
    const niveis = niveisData.map(nivel => ({
      nivel: nivel.nivel,
      nome: nivel.nome,
      descricao: nivel.descricao,
      steps: (nivel.steps || []).map(step => ({
        nome: step.nome,
        descricao: step.descricao,
        pontuacao_ideal: 0
      }))
    }));
    
    return {
      disc_desejado: '',
      disc_percentages: null,
      nivel_hierarquico: 'pleno',
      niveis
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch evaluation template
        const templateRes = await evaluationsAPI.getTemplate();
        setEvaluationTemplate(templateRes.data);
        
        // Fetch job positions for dropdown
        setLoadingPositions(true);
        try {
          const positionsRes = await jobPositionsAPI.getAll();
          setJobPositions(positionsRes.data);
        } catch (err) {
          console.error('Error fetching positions:', err);
        } finally {
          setLoadingPositions(false);
        }
        
        // Check if coming from JobPositionList with a pre-selected position
        const fromPosition = location.state?.fromPosition;
        if (fromPosition && !isEditMode) {
          applyPositionToForm(fromPosition, templateRes.data);
          setSelectedPositionId(fromPosition.id);
        }
        
        // If edit mode, fetch job data
        if (isEditMode) {
          setLoadingJob(true);
          const jobRes = await jobsAPI.getById(jobId);
          const job = jobRes.data;
          setForm({
            titulo: job.titulo || '',
            descricao: job.descricao || '',
            localizacao: job.localizacao || '',
            tipo_contrato: job.tipo_contrato || 'CLT',
            publica: job.publica ?? true,
            destaque: job.destaque ?? false,
            data_expiracao: job.data_expiracao || '',
            perfil_ideal: job.perfil_ideal || initializePerfilIdeal(templateRes.data),
            cargo_id: job.cargo_id || null,
            num_vagas: job.num_vagas || 1,
            // Novos campos
            salario: job.salario || '',
            horario_trabalho: job.horario_trabalho || '',
            beneficios: job.beneficios || [],
            observacoes: job.observacoes || '',
            // Big Five
            bigfive_required: job.bigfive_required || 'none'
          });
          if (job.cargo_id) {
            setSelectedPositionId(job.cargo_id);
          }
          // If job has a calculated profile, show it
          if (job.perfil_ideal?.disc_percentages) {
            setCalculatedProfile(job.perfil_ideal);
            setQuestionnaireMode('questionnaire');
          }
          
          // Fetch selected candidates for this job
          setLoadingCandidates(true);
          try {
            const candidatesRes = await jobsAPI.getSelectedCandidates(jobId);
            setSelectedCandidates(candidatesRes.data.candidatos || []);
            setCandidatesStats({
              total: candidatesRes.data.total_selecionados || 0,
              aprovados: candidatesRes.data.aprovados || 0
            });
          } catch (err) {
            console.error('Error fetching selected candidates:', err);
          } finally {
            setLoadingCandidates(false);
          }
          
          // If job has a linked position (cargo), fetch its details
          if (job.cargo_id) {
            try {
              const positionRes = await jobPositionsAPI.getById(job.cargo_id);
              setLinkedPosition(positionRes.data);
            } catch (err) {
              console.error('Error fetching linked position:', err);
            }
          }
          
          // Check if should focus on perfil tab
          if (location.state?.focusTab === 'perfil') {
            setActiveTab('perfil');
          }
          
          setLoadingJob(false);
        } else if (!fromPosition) {
          // Initialize empty perfil_ideal for new job
          setForm(prev => ({
            ...prev,
            perfil_ideal: initializePerfilIdeal(templateRes.data)
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Erro ao carregar dados');
      }
    };
    fetchData();
  }, [jobId, isEditMode, location.state]);

  // Apply job position data to form
  const applyPositionToForm = (position, template) => {
    // Build description from position data
    let descricao = '';
    
    if (position.descricao_sumaria) {
      descricao += `**Missão do Cargo:**\n${position.descricao_sumaria}\n\n`;
    }
    
    if (position.responsabilidades?.length > 0) {
      descricao += `**Principais Responsabilidades:**\n`;
      position.responsabilidades.forEach(resp => {
        if (resp.categoria) {
          descricao += `\n${resp.categoria}:\n`;
          resp.itens?.forEach(item => {
            if (item) descricao += `• ${item}\n`;
          });
        }
      });
      descricao += '\n';
    }
    
    if (position.experiencia || position.escolaridade) {
      descricao += `**Pré-requisitos:**\n`;
      if (position.escolaridade) descricao += `• Escolaridade: ${position.escolaridade}\n`;
      if (position.escolaridade_desejavel) descricao += `• Desejável: ${position.escolaridade_desejavel}\n`;
      if (position.experiencia) descricao += `• Experiência: ${position.experiencia}\n`;
      if (position.informatica) descricao += `• Informática: ${position.informatica}\n`;
      descricao += '\n';
    }
    
    if (position.conhecimentos_especificos?.length > 0) {
      descricao += `**Conhecimentos Específicos:**\n`;
      position.conhecimentos_especificos.forEach(c => {
        if (c) descricao += `• ${c}\n`;
      });
      descricao += '\n';
    }
    
    if (position.habilidades_competencias?.length > 0) {
      descricao += `**Habilidades e Competências:**\n`;
      position.habilidades_competencias.forEach(h => {
        if (h) descricao += `• ${h}\n`;
      });
    }
    
    // Build perfil_ideal from position
    const perfilIdeal = initializePerfilIdeal(template);
    if (position.perfil_cargo?.disc_desejado) {
      perfilIdeal.disc_desejado = position.perfil_cargo.disc_desejado;
    }
    if (position.perfil_cargo?.nivel_experiencia) {
      perfilIdeal.nivel_hierarquico = position.perfil_cargo.nivel_experiencia;
      setNivelHierarquico(position.perfil_cargo.nivel_experiencia);
    }
    
    setForm(prev => ({
      ...prev,
      titulo: position.titulo,
      descricao: descricao.trim(),
      perfil_ideal: perfilIdeal,
      cargo_id: position.id
    }));
    
    toast.success(`Dados do cargo "${position.titulo}" carregados`);
  };

  // Handle position selection change
  const handlePositionChange = (positionId) => {
    setSelectedPositionId(positionId);
    
    if (positionId === 'manual') {
      // Clear form for manual entry
      if (!isEditMode) {
        setForm(prev => ({
          ...prev,
          titulo: '',
          descricao: '',
          perfil_ideal: initializePerfilIdeal(evaluationTemplate),
          cargo_id: null
        }));
      }
      return;
    }
    
    const position = jobPositions.find(p => p.id === positionId);
    if (position) {
      applyPositionToForm(position, evaluationTemplate);
    }
  };

  // Fetch DISC questionnaire when level changes
  const fetchQuestionnaire = async (nivel) => {
    setLoadingQuestionnaire(true);
    try {
      const response = await discAPI.getQuestionnaire(nivel);
      setDiscQuestionnaire(response.data);
      setQuestionnaireAnswers({});
      setQuestionnaireStep(1);
    } catch (error) {
      toast.error('Erro ao carregar questionário');
    } finally {
      setLoadingQuestionnaire(false);
    }
  };

  // Calculate DISC profile from answers
  const calculateProfile = async () => {
    try {
      const response = await discAPI.calculateProfile({
        nivel_hierarquico: nivelHierarquico,
        respostas: questionnaireAnswers
      });
      setCalculatedProfile(response.data);
      
      // Update form with calculated profile
      setForm(prev => ({
        ...prev,
        perfil_ideal: {
          ...prev.perfil_ideal,
          disc_desejado: response.data.perfil_codigo,
          disc_percentages: response.data.disc_percentages,
          nivel_hierarquico: nivelHierarquico
        }
      }));
      
      setQuestionnaireStep(26); // Show results
    } catch (error) {
      toast.error('Erro ao calcular perfil');
    }
  };

  // Get all questions as a flat array
  const getAllQuestions = () => {
    if (!discQuestionnaire) return [];
    const q = discQuestionnaire.questionario;
    return [...(q.base || []), ...(q.nivel || []), ...(q.comportamental || [])];
  };

  const totalQuestions = discQuestionnaire?.total_perguntas || 25;
  const currentQuestion = getAllQuestions()[questionnaireStep - 1];
  const answeredCount = Object.keys(questionnaireAnswers).length;

  const updateStepScore = (nivelIndex, stepIndex, score) => {
    if (!form.perfil_ideal) return;
    
    const newPerfilIdeal = { ...form.perfil_ideal };
    newPerfilIdeal.niveis = [...newPerfilIdeal.niveis];
    newPerfilIdeal.niveis[nivelIndex] = { ...newPerfilIdeal.niveis[nivelIndex] };
    newPerfilIdeal.niveis[nivelIndex].steps = [...newPerfilIdeal.niveis[nivelIndex].steps];
    newPerfilIdeal.niveis[nivelIndex].steps[stepIndex] = {
      ...newPerfilIdeal.niveis[nivelIndex].steps[stepIndex],
      pontuacao_ideal: score
    };
    
    setForm({ ...form, perfil_ideal: newPerfilIdeal });
  };

  const calculateNivelMedia = (nivel) => {
    if (!nivel?.steps?.length) return '0.0';
    const total = nivel.steps.reduce((sum, s) => sum + (s.pontuacao_ideal || 0), 0);
    return (total / nivel.steps.length).toFixed(1);
  };

  const calculateOverallMedia = () => {
    if (!form.perfil_ideal?.niveis?.length) return '0.0';
    let total = 0;
    let count = 0;
    form.perfil_ideal.niveis.forEach(nivel => {
      nivel.steps.forEach(step => {
        total += step.pontuacao_ideal || 0;
        count++;
      });
    });
    return count > 0 ? (total / count).toFixed(1) : '0.0';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.titulo || !form.descricao || !form.localizacao) {
      toast.error('Preencha todos os campos obrigatórios');
      setActiveTab('info');
      return;
    }
    
    setLoading(true);
    try {
      if (isEditMode) {
        await jobsAPI.update(jobId, form);
        toast.success('Vaga atualizada com sucesso!');
      } else {
        await jobsAPI.create(form);
        toast.success('Vaga criada com sucesso!');
      }
      navigate('/company/jobs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar vaga');
    } finally {
      setLoading(false);
    }
  };

  if (loadingJob) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="max-w-5xl mx-auto" data-testid="create-job">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/company/jobs')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Briefcase className="w-6 h-6" />
              {isEditMode ? 'Editar Vaga' : 'Nova Vaga'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full grid grid-cols-2 rounded-none border-b">
                <TabsTrigger value="info" className="py-4 data-[state=active]:bg-cyan-50">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Informações da Vaga
                </TabsTrigger>
                <TabsTrigger value="perfil" className="py-4 data-[state=active]:bg-cyan-50">
                  <User className="w-4 h-4 mr-2" />
                  Perfil Ideal do Candidato
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: Job Information */}
              <TabsContent value="info" className="p-6 space-y-6 mt-0">
                {/* Job Position Selector */}
                <Card className="border-cyan-200 bg-gradient-to-r from-cyan-50 to-cyan-100">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-cyan-600 mt-0.5" />
                      <div className="flex-1">
                        <Label className="font-medium text-cyan-900">Cargo (Engenharia de Cargo)</Label>
                        <p className="text-sm text-cyan-700 mb-3">
                          Selecione um cargo da Engenharia para preencher automaticamente os dados da vaga
                        </p>
                        <Select 
                          value={selectedPositionId} 
                          onValueChange={handlePositionChange}
                          disabled={loadingPositions}
                        >
                          <SelectTrigger className="bg-white" data-testid="cargo-selector">
                            <SelectValue placeholder={loadingPositions ? "Carregando..." : "Selecione um cargo..."} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual">
                              <span className="flex items-center gap-2">
                                <AlertCircle className="w-4 h-4 text-slate-400" />
                                Preencher manualmente
                              </span>
                            </SelectItem>
                            {jobPositions.map(position => (
                              <SelectItem key={position.id} value={position.id}>
                                <span className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-cyan-600" />
                                  {position.titulo}
                                  {position.perfil_cargo?.disc_desejado && (
                                    <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">
                                      DISC: {position.perfil_cargo.disc_desejado}
                                    </Badge>
                                  )}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {jobPositions.length === 0 && !loadingPositions && (
                          <p className="text-xs text-cyan-600 mt-2">
                            Nenhum cargo cadastrado. <button 
                              type="button"
                              onClick={() => navigate('/company/job-positions/new')}
                              className="underline font-medium"
                            >
                              Criar cargo na Engenharia
                            </button>
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="space-y-2">
                  <Label htmlFor="titulo">Título da Vaga *</Label>
                  <Input
                    id="titulo"
                    value={form.titulo}
                    onChange={(e) => setForm({...form, titulo: e.target.value})}
                    placeholder="Ex: Desenvolvedor Full Stack"
                    data-testid="job-titulo"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descricao">Descrição *</Label>
                  <Textarea
                    id="descricao"
                    value={form.descricao}
                    onChange={(e) => setForm({...form, descricao: e.target.value})}
                    placeholder="Descreva as responsabilidades, requisitos e benefícios..."
                    rows={6}
                    data-testid="job-descricao"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="localizacao">Localização *</Label>
                    <Input
                      id="localizacao"
                      value={form.localizacao}
                      onChange={(e) => setForm({...form, localizacao: e.target.value})}
                      placeholder="Ex: São Paulo, SP"
                      data-testid="job-localizacao"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tipo_contrato">Tipo de Contrato</Label>
                    <Select
                      value={form.tipo_contrato}
                      onValueChange={(v) => setForm({...form, tipo_contrato: v})}
                    >
                      <SelectTrigger data-testid="job-tipo-contrato">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CLT">CLT</SelectItem>
                        <SelectItem value="PJ">PJ</SelectItem>
                        <SelectItem value="Estágio">Estágio</SelectItem>
                        <SelectItem value="Temporário">Temporário</SelectItem>
                        <SelectItem value="Freelancer">Freelancer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data_expiracao">Data de Expiração (opcional)</Label>
                  <Input
                    id="data_expiracao"
                    type="date"
                    value={form.data_expiracao}
                    onChange={(e) => setForm({...form, data_expiracao: e.target.value})}
                    data-testid="job-data-expiracao"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Vaga Pública</Label>
                    <p className="text-sm text-slate-500">Visível para todos os candidatos</p>
                  </div>
                  <Switch
                    checked={form.publica}
                    onCheckedChange={(v) => setForm({...form, publica: v})}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Vaga em Destaque</Label>
                    <p className="text-sm text-slate-500">Aparecer no topo das buscas</p>
                  </div>
                  <Switch
                    checked={form.destaque}
                    onCheckedChange={(v) => setForm({...form, destaque: v})}
                  />
                </div>

                {/* Quantidade de Vagas */}
                <div className="space-y-2">
                  <Label htmlFor="num_vagas">Quantidade de Vagas</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="num_vagas"
                      type="number"
                      min="1"
                      max="100"
                      value={form.num_vagas}
                      onChange={(e) => setForm({...form, num_vagas: parseInt(e.target.value) || 1})}
                      className="w-24"
                      data-testid="job-num-vagas"
                    />
                    <span className="text-sm text-slate-500">posição(ões) disponível(is)</span>
                  </div>
                </div>

                <Separator className="my-4" />

                {/* Salário */}
                <div className="space-y-2">
                  <Label htmlFor="salario">Faixa Salarial</Label>
                  <Input
                    id="salario"
                    value={form.salario}
                    onChange={(e) => setForm({...form, salario: e.target.value})}
                    placeholder="Ex: R$ 5.000 a R$ 7.000 ou A combinar"
                    data-testid="job-salario"
                  />
                  <p className="text-xs text-slate-500">Informe a faixa salarial ou deixe em branco para "A combinar"</p>
                </div>

                {/* Horário de Trabalho */}
                <div className="space-y-2">
                  <Label htmlFor="horario">Horário de Trabalho</Label>
                  <Input
                    id="horario"
                    value={form.horario_trabalho}
                    onChange={(e) => setForm({...form, horario_trabalho: e.target.value})}
                    placeholder="Ex: Segunda a Sexta, 09h às 18h"
                    data-testid="job-horario"
                  />
                </div>

                {/* Benefícios */}
                <div className="space-y-2">
                  <Label>Benefícios</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newBeneficio}
                      onChange={(e) => setNewBeneficio(e.target.value)}
                      placeholder="Ex: Vale Refeição, Plano de Saúde..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newBeneficio.trim()) {
                          e.preventDefault();
                          setForm({...form, beneficios: [...form.beneficios, newBeneficio.trim()]});
                          setNewBeneficio('');
                        }
                      }}
                      data-testid="job-beneficio-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (newBeneficio.trim()) {
                          setForm({...form, beneficios: [...form.beneficios, newBeneficio.trim()]});
                          setNewBeneficio('');
                        }
                      }}
                      data-testid="add-beneficio-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {form.beneficios.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {form.beneficios.map((beneficio, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="flex items-center gap-1 px-3 py-1"
                        >
                          {beneficio}
                          <button
                            type="button"
                            onClick={() => setForm({
                              ...form,
                              beneficios: form.beneficios.filter((_, i) => i !== index)
                            })}
                            className="ml-1 text-slate-400 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Pressione Enter ou clique + para adicionar</p>
                </div>

                {/* Observações do Recrutador */}
                <div className="space-y-2">
                  <Label htmlFor="observacoes">Observações Internas</Label>
                  <Textarea
                    id="observacoes"
                    value={form.observacoes}
                    onChange={(e) => setForm({...form, observacoes: e.target.value})}
                    placeholder="Anotações internas sobre a vaga (não visível para candidatos)"
                    rows={3}
                    data-testid="job-observacoes"
                  />
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Estas observações são privadas e não serão exibidas aos candidatos
                  </p>
                </div>

                <Separator className="my-4" />

                {/* Avaliações Comportamentais */}
                <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-violet-50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-purple-800">
                      <Brain className="w-5 h-5" />
                      Avaliações Comportamentais
                    </CardTitle>
                    <p className="text-sm text-purple-600">
                      Exija testes comportamentais dos candidatos para esta vaga
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Big Five */}
                    <div className="p-4 bg-white rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                            <Lightbulb className="w-5 h-5 text-violet-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">Big Five (OCEAN)</p>
                            <p className="text-xs text-slate-500">Avaliação de personalidade</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Label className="text-sm text-slate-700">Exigir Big Five para esta vaga?</Label>
                        <RadioGroup
                          value={form.bigfive_required || 'none'}
                          onValueChange={(v) => setForm({...form, bigfive_required: v})}
                          className="grid grid-cols-1 gap-2"
                        >
                          <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                            <RadioGroupItem value="none" id="bigfive-none" />
                            <Label htmlFor="bigfive-none" className="flex-1 cursor-pointer">
                              <span className="font-medium">Não exigir</span>
                              <p className="text-xs text-slate-500">Candidatos não precisam fazer o teste</p>
                            </Label>
                          </div>
                          <div className="flex items-center space-x-3 p-3 rounded-lg border border-violet-200 bg-violet-50 hover:bg-violet-100 cursor-pointer">
                            <RadioGroupItem value="BFI-44" id="bigfive-44" />
                            <Label htmlFor="bigfive-44" className="flex-1 cursor-pointer">
                              <span className="font-medium text-violet-900">BFI-44 (Padrão)</span>
                              <p className="text-xs text-violet-600">44 perguntas • ~10 minutos</p>
                            </Label>
                            <Badge className="bg-violet-600 text-white text-xs">Recomendado</Badge>
                          </div>
                          <div className="flex items-center space-x-3 p-3 rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 cursor-pointer">
                            <RadioGroupItem value="NEO-PI-R" id="bigfive-60" />
                            <Label htmlFor="bigfive-60" className="flex-1 cursor-pointer">
                              <span className="font-medium text-purple-900">NEO-PI-R (Completo)</span>
                              <p className="text-xs text-purple-600">60 perguntas • ~15 minutos • Maior precisão</p>
                            </Label>
                            <Badge variant="outline" className="border-purple-400 text-purple-700 text-xs">Cargos sênior</Badge>
                          </div>
                        </RadioGroup>
                        
                        {form.bigfive_required && form.bigfive_required !== 'none' && (
                          <div className="mt-3 p-3 bg-violet-100 rounded-lg text-sm text-violet-800">
                            <p className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Candidatos receberão uma notificação para realizar o teste Big Five ao se candidatar.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Candidatos Selecionados - Apenas em modo de edição */}
                {isEditMode && (
                  <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span className="flex items-center gap-2 text-green-800">
                          <Users className="w-5 h-5" />
                          Candidatos Selecionados
                        </span>
                        <div className="flex items-center gap-2">
                          <Badge className="bg-green-600 text-white">
                            {candidatesStats.aprovados} aprovado(s)
                          </Badge>
                          <Badge variant="outline" className="border-green-400 text-green-700">
                            {candidatesStats.total} / {form.num_vagas} vaga(s)
                          </Badge>
                        </div>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loadingCandidates ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                        </div>
                      ) : selectedCandidates.length === 0 ? (
                        <p className="text-sm text-green-700 text-center py-4">
                          Nenhum candidato selecionado ainda. Acesse a aba de candidatos para selecionar.
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {selectedCandidates.map((candidate) => (
                            <div 
                              key={candidate.candidate_id}
                              className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900">{candidate.cargo || 'Candidato'}</p>
                                  <div className="flex items-center gap-2">
                                    {candidate.disc_profile && (
                                      <Badge className="bg-purple-100 text-purple-700 text-xs">
                                        <Brain className="w-3 h-3 mr-1" />
                                        {candidate.disc_profile}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Select
                                  value={candidate.status}
                                  onValueChange={async (newStatus) => {
                                    try {
                                      await jobsAPI.updateCandidateStatus(jobId, candidate.candidate_id, newStatus);
                                      setSelectedCandidates(prev => prev.map(c => 
                                        c.candidate_id === candidate.candidate_id 
                                          ? { ...c, status: newStatus }
                                          : c
                                      ));
                                      setCandidatesStats(prev => ({
                                        ...prev,
                                        aprovados: selectedCandidates.filter(c => 
                                          c.candidate_id === candidate.candidate_id 
                                            ? newStatus === 'aprovado'
                                            : c.status === 'aprovado'
                                        ).length
                                      }));
                                      toast.success(`Status atualizado para: ${newStatus}`);
                                    } catch (error) {
                                      toast.error('Erro ao atualizar status');
                                    }
                                  }}
                                >
                                  <SelectTrigger className={`w-32 ${
                                    candidate.status === 'aprovado' ? 'border-green-400 bg-green-50' :
                                    candidate.status === 'reprovado' ? 'border-red-400 bg-red-50' :
                                    'border-amber-400 bg-amber-50'
                                  }`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="em_analise">
                                      <span className="flex items-center gap-2 text-amber-700">
                                        <div className="w-2 h-2 bg-amber-500 rounded-full" />
                                        Em Análise
                                      </span>
                                    </SelectItem>
                                    <SelectItem value="aprovado">
                                      <span className="flex items-center gap-2 text-green-700">
                                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                                        Aprovado
                                      </span>
                                    </SelectItem>
                                    <SelectItem value="reprovado">
                                      <span className="flex items-center gap-2 text-red-700">
                                        <div className="w-2 h-2 bg-red-500 rounded-full" />
                                        Reprovado
                                      </span>
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Tab 2: Ideal Candidate Profile */}
              <TabsContent value="perfil" className="p-6 mt-0">
                <div className="space-y-6">
                  {/* Linked Position - Show when job has a linked cargo */}
                  {linkedPosition ? (
                    <Card className="border-purple-300 bg-gradient-to-r from-purple-50 to-indigo-50 shadow-md">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center justify-between">
                          <span className="flex items-center gap-2 text-purple-800">
                            <FileText className="w-5 h-5" />
                            Perfil do Cargo: {linkedPosition.titulo}
                          </span>
                          <Badge className="bg-purple-600 text-white">
                            Engenharia de Cargo
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* DISC Profile */}
                        {linkedPosition.perfil_cargo?.disc_desejado ? (
                          <div className="flex items-center gap-4 p-3 bg-white rounded-lg border border-purple-200">
                            <div className="flex items-center gap-2">
                              <Brain className="w-5 h-5 text-purple-600" />
                              <span className="font-medium text-purple-900">Perfil DISC Desejado:</span>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700 text-lg px-4 py-1">
                              {linkedPosition.perfil_cargo.disc_desejado}
                            </Badge>
                            {linkedPosition.perfil_cargo.nivel_experiencia && (
                              <Badge variant="outline" className="border-purple-300 text-purple-700">
                                Nível: {linkedPosition.perfil_cargo.nivel_experiencia}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <p className="text-amber-700 flex items-center gap-2">
                              <AlertCircle className="w-5 h-5" />
                              O perfil DISC ainda não foi definido para este cargo.
                            </p>
                          </div>
                        )}
                        
                        {/* Key Requirements */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {linkedPosition.escolaridade && (
                            <div className="p-3 bg-white rounded-lg border border-purple-200">
                              <span className="text-sm font-medium text-purple-700">Escolaridade:</span>
                              <p className="text-slate-700">{linkedPosition.escolaridade}</p>
                            </div>
                          )}
                          {linkedPosition.experiencia && (
                            <div className="p-3 bg-white rounded-lg border border-purple-200">
                              <span className="text-sm font-medium text-purple-700">Experiência:</span>
                              <p className="text-slate-700">{linkedPosition.experiencia}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Skills/Competencies */}
                        {linkedPosition.habilidades_competencias?.length > 0 && (
                          <div className="p-3 bg-white rounded-lg border border-purple-200">
                            <span className="text-sm font-medium text-purple-700 block mb-2">Habilidades e Competências:</span>
                            <div className="flex flex-wrap gap-2">
                              {linkedPosition.habilidades_competencias.filter(h => h).map((hab, i) => (
                                <Badge key={i} variant="outline" className="border-purple-300 text-purple-700">
                                  {hab}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Profile Description */}
                        {linkedPosition.perfil_cargo?.descricao_perfil && (
                          <div className="p-3 bg-white rounded-lg border border-purple-200">
                            <span className="text-sm font-medium text-purple-700 block mb-1">Descrição do Perfil Ideal:</span>
                            <p className="text-slate-600 text-sm">{linkedPosition.perfil_cargo.descricao_perfil}</p>
                          </div>
                        )}
                        
                        <div className="flex gap-3">
                          <Button
                            onClick={() => navigate(`/company/job-positions/${linkedPosition.id}/edit`, { state: { focusSection: 'perfil' } })}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Brain className="w-4 h-4 mr-2" />
                            Editar Perfil DISC
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/company/job-positions/${linkedPosition.id}`)}
                            className="border-purple-400 text-purple-700 hover:bg-purple-100"
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            Ver Engenharia de Cargo
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    /* No linked cargo - Show message to link one */
                    <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50">
                      <CardContent className="py-12 text-center">
                        <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-amber-900 mb-2">
                          Nenhum Cargo Vinculado
                        </h3>
                        <p className="text-amber-700 mb-6 max-w-md mx-auto">
                          Para definir o Perfil Ideal do candidato, primeiro vincule esta vaga a um cargo da Engenharia de Cargo.
                          O perfil DISC é definido na descrição do cargo.
                        </p>
                        <div className="flex justify-center gap-3">
                          <Button
                            onClick={() => setActiveTab('info')}
                            variant="outline"
                            className="border-amber-400 text-amber-700 hover:bg-amber-100"
                          >
                            Vincular Cargo Existente
                          </Button>
                          <Button
                            onClick={() => navigate('/company/job-positions/new')}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Criar Novo Cargo
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            {/* Submit Button */}
            <div className="p-6 border-t bg-slate-50">
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/company/jobs')}
                  className="flex-1 border-slate-400 text-slate-700 hover:bg-slate-100"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                  data-testid="submit-job"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  {isEditMode ? 'Salvar Alterações' : 'Criar Vaga'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
